import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useMemo, useRef, useState } from 'react'

import {
  type OrderDraftItemMutationStatusEnum,
  OrderDraftItemMutationStatuses,
} from './OrderDraftItemMutationStatusEnum'

export type OrderDraftItemMutationVisibleStatus = Exclude<
  OrderDraftItemMutationStatusEnum,
  typeof OrderDraftItemMutationStatuses.COMPLETED
>

type InProgressStatus =
  | typeof OrderDraftItemMutationStatuses.CREATING
  | typeof OrderDraftItemMutationStatuses.UPDATING
  | typeof OrderDraftItemMutationStatuses.DELETING

type FailedStatus =
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_CREATE
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_UPDATE
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_DELETE

type TerminalStatus =
  | typeof OrderDraftItemMutationStatuses.COMPLETED
  | FailedStatus

/** A silent write reports nothing while in flight, but still settles like any other. */
export type SilentItemWriteOutcome = 'settled' | 'failed'

interface OrderDraftItemMutationEntry {
  status: OrderDraftItemMutationVisibleStatus
  latestMutationSeq: number
}

// No `| undefined` value: a settled item's key is deleted, never set to undefined, and
// `getItemsWithUnsettledMutations` reads key-presence alone.
type OrderDraftItemMutationStatusMap = Record<
  number,
  OrderDraftItemMutationEntry
>

// Overload signature for setItemMutationStatus to better type the function when
// called with InProgressStatus or TerminalStatus.
interface SetItemMutationStatus {
  (itemId: number, status: InProgressStatus): number
  (itemId: number, status: TerminalStatus, seq: number): void
}

interface IOrderDraftItemMutationStatusContextValue {
  getItemMutationStatus: (
    itemId: number
  ) => OrderDraftItemMutationVisibleStatus | null
  setItemMutationStatus: SetItemMutationStatus
  hasInFlightItemMutations: boolean
  /**
   * Returns the IDs of all items currently in a failed state
   * (FAILED_TO_CREATE, FAILED_TO_UPDATE, or FAILED_TO_DELETE).
   * Reads the latest committed state synchronously, so it's safe to call from
   * a closure without closure-staleness.
   */
  getItemsWithFailedMutations: () => number[]
  /**
   * IDs of items whose latest mutation has not completed successfully (in-flight or
   * failed). Safe to call from a closure.
   */
  getItemsWithUnsettledMutations: () => number[]
  /**
   * IDs of items with a mutation in flight — a visible in-progress status or a booked silent
   * write. Narrower than `getItemsWithUnsettledMutations`, which also names items whose
   * terminal failure keeps its entry. Safe to call from a closure.
   */
  getItemsWithInProgressMutations: () => number[]
  /**
   * Ticks once per settled mutation round trip, so a background merge can tell whether its
   * snapshot predates a local write. Safe to call from a closure.
   */
  getSettledMutationCount: () => number
  // Use when the item leaves the cache outright; bypasses seq guarding
  // because there's no in-flight mutation to honor anymore.
  clearItemMutationStatus: (itemId: number) => void
  /**
   * Books a write the user never asked for: invisible to the readers that drive card treatment
   * and bulk actions while it is in flight, but a `'failed'` outcome marks the item
   * FAILED_TO_UPDATE like any other failed write. The caller must invoke the returned
   * deregister from a `finally` — nothing sweeps a booking that is never released.
   */
  registerSilentItemWrite: (
    itemId: number
  ) => (outcome: SilentItemWriteOutcome) => void
}

export const OrderDraftItemMutationStatusContext =
  createContext<IOrderDraftItemMutationStatusContextValue | null>(null)

const isInProgressStatus = (
  status: OrderDraftItemMutationStatusEnum
): status is InProgressStatus =>
  status === OrderDraftItemMutationStatuses.CREATING ||
  status === OrderDraftItemMutationStatuses.UPDATING ||
  status === OrderDraftItemMutationStatuses.DELETING

const isFailedStatus = (
  status: OrderDraftItemMutationStatusEnum
): status is FailedStatus =>
  status === OrderDraftItemMutationStatuses.FAILED_TO_CREATE ||
  status === OrderDraftItemMutationStatuses.FAILED_TO_UPDATE ||
  status === OrderDraftItemMutationStatuses.FAILED_TO_DELETE

export const OrderDraftItemMutationStatusContextProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [mutationStatuses, setMutationStatuses] =
    useState<OrderDraftItemMutationStatusMap>({})

  // Monotonically increasing counter for mutation ordering.
  // Lives in a ref so setItemMutationStatus can return the
  // assigned sequence synchronously.
  const seqCounterRef = useRef(0)

  // Counts settles rather than naming ids: a settled mutation's status entry is already gone.
  const settledMutationCountRef = useRef(0)

  const hasInFlightItemMutations = useMemo<boolean>(
    () =>
      Object.values(mutationStatuses).some(
        (entry) => entry !== undefined && isInProgressStatus(entry.status)
      ),
    [mutationStatuses]
  )

  // Mirror of mutationStatuses in a ref so getItemsWithFailedMutations can
  // stay stable across renders while still reading the latest committed
  // value — important as this may be called from a closure
  const mutationStatusesRef = useRef(mutationStatuses)
  mutationStatusesRef.current = mutationStatuses

  // A ref, not state: re-rendering is what a silent write must not do. Counted, not a Set:
  // the first of two overlapping writes to release must not cover the second.
  const silentWriteCountsRef = useRef<Map<number, number>>(new Map())

  // Latest seq booked per item, kept past the status entry's own lifetime. A COMPLETED write
  // deletes the entry and a silent write never creates one, so this is the only way to tell
  // "no mutation was ever booked for this item" from "a newer one already finished".
  const latestItemMutationSeqRef = useRef<Map<number, number>>(new Map())

  const getItemMutationStatus = useCallback(
    (itemId: number): OrderDraftItemMutationVisibleStatus | null => {
      return mutationStatuses[itemId]?.status ?? null
    },
    [mutationStatuses]
  )

  // Terminal writes carry the seq from the originating in-progress call, and
  // any write whose seq is older than the latest is dropped — so stale
  // completions/failures cannot clobber the state of a newer in-flight mutation.
  //
  // useMemo (rather than useCallback) because useCallback's inferred type does
  // not preserve function declaration overloads.
  const setItemMutationStatus = useMemo<SetItemMutationStatus>(() => {
    function impl(itemId: number, status: InProgressStatus): number
    function impl(itemId: number, status: TerminalStatus, seq: number): void
    function impl(
      itemId: number,
      status: OrderDraftItemMutationStatusEnum,
      seq?: number
    ): number | void {
      if (isInProgressStatus(status)) {
        const newSeq = ++seqCounterRef.current
        latestItemMutationSeqRef.current.set(itemId, newSeq)

        setMutationStatuses((prev) => ({
          ...prev,
          [itemId]: { status, latestMutationSeq: newSeq },
        }))

        return newSeq
      }

      if (typeof seq !== 'number') {
        throw new TypeError(
          `setItemMutationStatus: terminal status "${status}" requires a seq argument`
        )
      }

      // Outside the updater (React may call it twice), and counted even when the write below
      // is dropped as stale — a round trip still finished.
      settledMutationCountRef.current += 1

      setMutationStatuses((prev) => {
        const current = prev[itemId]

        // Drop stale write if...
        // 1. No active mutation
        if (!current) return prev

        // 2. The write's seq is not the latest
        if (seq < current.latestMutationSeq) return prev

        // Otherwise, update the stored status
        switch (status) {
          case OrderDraftItemMutationStatuses.COMPLETED: {
            const { [itemId]: _, ...rest } = prev
            return rest
          }
          case OrderDraftItemMutationStatuses.FAILED_TO_CREATE:
          case OrderDraftItemMutationStatuses.FAILED_TO_UPDATE:
          case OrderDraftItemMutationStatuses.FAILED_TO_DELETE:
            return {
              ...prev,
              [itemId]: { status, latestMutationSeq: seq },
            }
          default: {
            const _exhaustiveCheck: never = status
            return _exhaustiveCheck
          }
        }
      })
    }

    return impl
  }, [])

  const getItemsWithFailedMutations = useCallback((): number[] => {
    return Object.entries(mutationStatusesRef.current)
      .filter(
        ([, entry]) => entry !== undefined && isFailedStatus(entry.status)
      )
      .map(([id]) => Number(id))
  }, [])

  // No status predicate needed: a COMPLETED write deletes the entry, so any id that
  // still has one is unsettled.
  const getItemsWithUnsettledMutations = useCallback((): number[] => {
    return [
      ...new Set([
        ...Object.keys(mutationStatusesRef.current).map(Number),
        ...silentWriteCountsRef.current.keys(),
      ]),
    ]
  }, [])

  const getItemsWithInProgressMutations = useCallback((): number[] => {
    const inProgressItemIds = Object.entries(mutationStatusesRef.current)
      .filter(
        ([, entry]) => entry !== undefined && isInProgressStatus(entry.status)
      )
      .map(([id]) => Number(id))

    return [
      ...new Set([
        ...inProgressItemIds,
        ...silentWriteCountsRef.current.keys(),
      ]),
    ]
  }, [])

  // Not ticked by clearItemMutationStatus: client-only items never reach the server.
  const getSettledMutationCount = useCallback(
    (): number => settledMutationCountRef.current,
    []
  )

  const registerSilentItemWrite = useCallback((itemId: number) => {
    const counts = silentWriteCountsRef.current
    counts.set(itemId, (counts.get(itemId) ?? 0) + 1)

    // Booked here so a failure can be seq-guarded like any other terminal write.
    const seq = ++seqCounterRef.current
    latestItemMutationSeqRef.current.set(itemId, seq)

    let hasDeregistered = false

    return (outcome: SilentItemWriteOutcome) => {
      if (hasDeregistered) return
      hasDeregistered = true

      const remaining = (counts.get(itemId) ?? 1) - 1

      if (remaining > 0) counts.set(itemId, remaining)
      else counts.delete(itemId)

      // Ticked for either outcome, and even when the write below is dropped as stale — a
      // round trip still finished.
      settledMutationCountRef.current += 1

      // Stale: a newer mutation for this item has been booked since, so it owns the item's
      // status and neither outcome may overwrite it. Mirrors setItemMutationStatus's seq
      // guard, which cannot be reused here — it drops a terminal write when no entry
      // exists, and a silent write deliberately never made one.
      if ((latestItemMutationSeqRef.current.get(itemId) ?? seq) > seq) return

      setMutationStatuses((prev) => {
        const current = prev[itemId]

        // A visible mutation still in flight owns the item's status, whichever of the two
        // was booked first. Overwriting it would drop the card's loading treatment and
        // leave that mutation's own terminal write to be discarded as orphaned.
        if (current && isInProgressStatus(current.status)) return prev

        // Retracts what this write's own failures set, the way a COMPLETED visible write
        // clears its entry: the item reached the DB, so it is no longer failed. A failed
        // create or delete belongs to another write and is not ours to clear.
        if (outcome === 'settled') {
          if (
            current?.status !== OrderDraftItemMutationStatuses.FAILED_TO_UPDATE
          ) {
            return prev
          }

          const { [itemId]: _, ...rest } = prev

          return rest
        }

        // Relabelling another write's failure points the user at the wrong retry.
        if (
          current?.status === OrderDraftItemMutationStatuses.FAILED_TO_CREATE ||
          current?.status === OrderDraftItemMutationStatuses.FAILED_TO_DELETE
        ) {
          return prev
        }

        return {
          ...prev,
          [itemId]: {
            status: OrderDraftItemMutationStatuses.FAILED_TO_UPDATE,
            latestMutationSeq: seq,
          },
        }
      })
    }
  }, [])

  const clearItemMutationStatus = useCallback((itemId: number): void => {
    setMutationStatuses((prev) => {
      if (!prev[itemId]) return prev
      const { [itemId]: _, ...rest } = prev
      return rest
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      getItemMutationStatus,
      setItemMutationStatus,
      hasInFlightItemMutations,
      getItemsWithFailedMutations,
      getItemsWithUnsettledMutations,
      getItemsWithInProgressMutations,
      getSettledMutationCount,
      clearItemMutationStatus,
      registerSilentItemWrite,
    }),
    [
      getItemMutationStatus,
      setItemMutationStatus,
      hasInFlightItemMutations,
      getItemsWithFailedMutations,
      getItemsWithUnsettledMutations,
      getItemsWithInProgressMutations,
      getSettledMutationCount,
      clearItemMutationStatus,
      registerSilentItemWrite,
    ]
  )

  return (
    <OrderDraftItemMutationStatusContext.Provider value={contextValue}>
      {children}
    </OrderDraftItemMutationStatusContext.Provider>
  )
}
